import {
  ChainTypeError,
  ConnectorAccountNotFoundError,
  ConnectorNotConnectedError,
  sendSolanaTransaction,
  type ChainIdParameter,
  type Connection,
  type ConnectorParameter,
  type Evaluate,
  type SolanaChain,
  type SolanaProvider,
  type SolanaWalletClient,
} from '@particle-network/connector-core';
import type { SendOptions, Transaction, TransactionSignature, VersionedTransaction } from '@solana/web3.js';
import type { Config } from '../createConfig';

export type GetSolanaWalletClientParameters = Evaluate<
  ChainIdParameter &
    ConnectorParameter & {
      account?: string | undefined;
    }
>;

export const getSolanaWalletClient = async (
  config: Config,
  parameters: GetSolanaWalletClientParameters = {}
): Promise<SolanaWalletClient> => {
  let connection: Connection | undefined;
  if (parameters.connector) {
    const { connector } = parameters;
    const [accounts, chainId] = await Promise.all([connector.getAccounts(), connector.getChainId()]);
    connection = {
      accounts: accounts as readonly [string, ...string[]],
      chainId,
      connector,
    };
  } else connection = config.state.connections.get(config.state.current!);
  if (!connection) throw new ConnectorNotConnectedError();

  const chainId = parameters.chainId ?? connection.chainId;
  const connector = connection.connector;
  if (connector.chainType !== 'solana') {
    throw new ChainTypeError();
  }

  const provider = (await connector.getProvider()) as SolanaProvider;

  const account = parameters.account ?? connection.accounts[0];
  const chain = config.chains.find((chain) => chain.id === chainId);

  const clientConnection = config
    .getWalletConnector(connector.walletConnectorType)!
    .getClient<SolanaChain>(chain?.id || 101);

  // if account was provided, check that it exists on the connector
  if (parameters.account && !connection.accounts.some((x) => x.toLowerCase() === account.toLowerCase()))
    throw new ConnectorAccountNotFoundError({
      address: account,
      connector,
    });

  return {
    publicKey: provider.publicKey!,
    chain: chain!,
    async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
      return await provider.signTransaction(transaction);
    },
    async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
      return await provider.signAllTransactions(transactions);
    },
    async sendTransaction<T extends Transaction | VersionedTransaction>(
      transaction: T,
      options: SendOptions = {}
    ): Promise<{
      signature: TransactionSignature;
    }> {
      if (connector.walletConnectorType === 'particleAuth') {
        const result = await provider.signAndSendTransaction!(transaction);
        return result;
      }

      return await sendSolanaTransaction(provider, transaction, clientConnection, options);
    },
    async signMessage(message: Uint8Array): Promise<{
      signature: Uint8Array;
    }> {
      const { signature } = await provider.signMessage(message);
      return { signature };
    },
    async switchChain(chainId: number) {
      return await connector.switchChain!({ chainId });
    },
  };
};
